// Angular Imports
import { NgModule } from '@angular/core';
import { Routes, RouterModule, PreloadAllModules } from '@angular/router';

// project import
import { AdminComponent } from './theme/layout/admin/admin.component';
import { UnauthorizedComponent } from './dashboard/unauthorized/unauthorized.component';
import { ForgotPasswordComponent } from './dashboard/forgot-password/forgot-password.component';
import { LoginComponent } from './dashboard/users/login/login.component';
import { NoAuthGuard } from './theme/shared/_helpers/no-auth-guard.service';
import { AuthGuard } from './theme/shared/_helpers/auth.guard';
import { AccessFunctionGuard } from './theme/shared/_helpers/access-fuction.guard';
import { DeviceRouteGuard } from './theme/shared/_helpers/device-route.guard';
import { pagesAccessFunctions } from './theme/shared/access-functions';
import { GuestComponent } from './theme/layout/guest/guest.component';
import { ChangePasswordComponent } from './dashboard/change-password/change-password.component';
import { UnblockLoginComponent } from './dashboard/users/unblock-login/unblock-login.component';
import { BecomeUserComponent } from './dashboard/users/become-user/become-user.component';
import { SimManagementComponent } from 'sim-management-frontend/dist/sim-management';

const routes: Routes = [
	{ path: '', pathMatch: 'full', redirectTo: 'dashboard' },
	// Outside both shells on purpose: NoAuthGuard would bounce a browser that already holds a
	// currentUser, and AuthGuard would block one that does not.
	{ path: 'become-user/:token', component: BecomeUserComponent },
	{
		path: '',
		component: GuestComponent,
		canActivate: [NoAuthGuard],
		children: [
			{
				path: 'login',
				component: LoginComponent,
				canActivate: [NoAuthGuard],
				children: []
			},
			{
				path: 'forgot-password',
				component: ForgotPasswordComponent,
				canActivate: [NoAuthGuard],
				children: []
			},
			{
				path: 'reset-password/:userId',
				component: ChangePasswordComponent,
				canActivate: [NoAuthGuard],
				data: { pageName: 'reset_password'},
				children: []
			},
			{
				path: 'unblock-login',
				component: UnblockLoginComponent,
				canActivate: [NoAuthGuard],
				children: []
			}
		]
	},
	{
		path: '',
		component: AdminComponent,
		canActivate: [AuthGuard],
		children: [
			{
				path: 'search-device-details',
				loadComponent: () => import('./generic/search-device-details/search-device-details.component').then((c) => c.SearchDeviceDetailsComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.search_device_details }
			},
			{
				path: 'dashboard',
				loadComponent: () => import('./dashboard/dashboard/dashboard.component').then((c) => c.DashboardComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.dashboard }
			},
			{
				path: 'devices/rma',
				loadComponent: () => import('./dashboard/devices/rma/rma.component').then((c) => c.RmaComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.rma }
			},
			{
				path: 'users',
				loadComponent: () => import('./dashboard/users/users.component').then((c) => c.UsersComponent),
				canActivate: [AccessFunctionGuard],
  				data: { accessFunction: pagesAccessFunctions.user_list }
			},
			{
				path: 'users/create',
				loadComponent: () => import('./dashboard/users/create-user/create-user.component').then((c) => c.CreateUserComponent),
				canActivate: [AccessFunctionGuard],
  				data: { accessFunction: pagesAccessFunctions.user_create }
			},
			{
				path: 'users/edit/:id',
				loadComponent: () => import('./dashboard/users/create-user/create-user.component').then((c) => c.CreateUserComponent),
				canActivate: [AccessFunctionGuard],
  				data: { accessFunction: pagesAccessFunctions.user_edit }
			},
			{
				path: 'users/access-functions/:id',
				loadComponent: () => import('./dashboard/users/access-functions/access-functions.component').then((c) => c.AccessFunctionsComponent),
				canActivate: [AccessFunctionGuard],
  				data: { accessFunction: pagesAccessFunctions.user_access_functions }
			},
			{
				path: 'rejected-devices-connection',
				loadComponent: () => import('./dashboard/devices/rejection-devices-connection/rejection-devices-connection.component').then((c) => c.RejectionDevicesConnectionComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.rejection_devices_logs }
			},
			{
				path: 'device-restart-count-report',
				loadComponent: () => import('./dashboard/devices/restart-count-report/restart-count-report.component').then((c) => c.RestartCountReportComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.restart_count_report }
			},
			{
				path: 'bwt-connectivity-report',
				loadComponent: () => import('./dashboard/devices/bwt-connectivity-report/bwt-connectivity-report.component').then((c) => c.BwtConnectivityReportComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.bwt_connectivity_report }
			},
			{
				path: 'device-replacement-log',
				loadComponent: () => import('./dashboard/devices/device-replacement-log/device-replacement-log.component').then((c) => c.DeviceReplacementLogComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.device_replacement_log }
			},
			{
				path: 'devices',
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.device_list },
				loadComponent: () => import('./dashboard/devices/devices.component').then(c => c.DevicesComponent),
			},
			{
				path: 'devices/:stage',
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.device_list },
				loadComponent: () => import('./dashboard/devices/devices.component').then(c => c.DevicesComponent),
			},
			{
				path: 'devices/:stage/create',
				loadComponent: () => import('./dashboard/devices/add-device/add-device.component').then((c) => c.AddDeviceComponent),
				canActivate: [AccessFunctionGuard],
  				data: { accessFunction: pagesAccessFunctions.device_create }
			},
			// temporary solution until fix all URLs
			{
				path: 'device-page/:id',
				loadComponent: () => import('./dashboard/devices/device-page/device-page.component').then((c) => c.DevicePageComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.device_page },
				children: [
					{
						path: '',
						redirectTo: 'define',
						pathMatch: 'full'
					},
					{
						path: 'define',
						loadComponent: () => import('./dashboard/devices/device-define/device-define.component').then((c) => c.DeviceDefineComponent),
						canActivate: [AccessFunctionGuard],
						data: { accessFunction: pagesAccessFunctions.device_define }
					},
				]
			},
			{
				path: 'device-page/:stage/:id',
				loadComponent: () => import('./dashboard/devices/device-page/device-page.component').then((c) => c.DevicePageComponent),
				canMatch: [DeviceRouteGuard],
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.device_page },
				children: [
					{
						path: '',
						redirectTo: 'define',
						pathMatch: 'full'
					},
					{
						path: 'settings',
						loadComponent: () => import('./dashboard/devices/device-settings/device-settings.component').then((c) => c.DeviceSettingsComponent),
						canActivate: [AccessFunctionGuard],
						data: { accessFunction: pagesAccessFunctions.device_settings }
					},
					{
						path: 'events',
						loadComponent: () => import('./dashboard/devices/device-events/device-events.component').then((c) => c.DeviceEventsComponent),
						canActivate: [AccessFunctionGuard],
						data: { accessFunction: pagesAccessFunctions.device_events }
					},
					{
						path: 'restart-count-history',
						loadComponent: () => import('./dashboard/devices/restart-count-history/restart-count-history.component').then((c) => c.RestartCountHistoryComponent),
						canActivate: [AccessFunctionGuard],
						data: { accessFunction: pagesAccessFunctions.restart_count_history }
					},
					{
						path: 'define',
						loadComponent: () => import('./dashboard/devices/device-define/device-define.component').then((c) => c.DeviceDefineComponent),
						canActivate: [AccessFunctionGuard],
						data: { accessFunction: pagesAccessFunctions.device_define }
					},
					{
						path: 'system-info',
						loadComponent: () => import('./dashboard/devices/system-info/system-info.component').then((c) => c.SystemInfoComponent),
						canActivate: [AccessFunctionGuard],
						data: { accessFunction: pagesAccessFunctions.system_info }
					},
					{
						path: 'cell-info',
						loadComponent: () => import('./dashboard/devices/cell-info/cell-info.component').then((c) => c.CellInfoComponent),
						canActivate: [AccessFunctionGuard],
						data: { accessFunction: pagesAccessFunctions.cell_info }
					},
					{
						path: 'device-notes',
						loadComponent: () => import('./dashboard/devices/device-notes/device-notes.component').then((c) => c.DeviceNotesComponent),
						canActivate: [AccessFunctionGuard],
						data: { accessFunction: pagesAccessFunctions.device_notes }
					},
					{
						path: 'device-replacement',
						loadComponent: () => import('./dashboard/devices/device-replacement/device-replacement.component').then((c) => c.DeviceReplacementComponent),
						canActivate: [AccessFunctionGuard],
						data: { accessFunction: pagesAccessFunctions.device_replacement }
					},
					{
						path: 'debug-records',
						loadComponent: () => import('./dashboard/devices/debug-records/debug-records.component').then((c) => c.DebugRecordsComponent),
						canActivate: [AccessFunctionGuard],
						data: { accessFunction: pagesAccessFunctions.debug_records }
					},
					{
						path: 'connectivity-track',
						loadComponent: () => import('./dashboard/devices/connectivity-track/connectivity-track.component').then((c) => c.ConnectivityTrackComponent),
						canActivate: [AccessFunctionGuard],
						data: { accessFunction: pagesAccessFunctions.connectivity_track }
					},
					{
						path: 'config-track',
						loadComponent: () => import('./dashboard/devices/config-track/config-track.component').then((c) => c.ConfigTrackComponent),
						canActivate: [AccessFunctionGuard],
						data: { accessFunction: pagesAccessFunctions.config_track }
					},
					{
						path: 'define-log',
						loadComponent: () => import('./dashboard/devices/define-log/define-log.component').then((c) => c.DefineLogComponent),
						canActivate: [AccessFunctionGuard],
						data: { accessFunction: pagesAccessFunctions.define_log }
					},
					{
						path: 'cellular-info-track',
						loadComponent: () => import('./dashboard/devices/device-info-tracking/cellular-info-track/cellular-info-track.component').then((c) => c.CellularInfoTrackComponent),
						canActivate: [AccessFunctionGuard],
						data: { accessFunction: pagesAccessFunctions.cellular_info_track }
					},
					{
						path: 'system-info-track',
						loadComponent: () => import('./dashboard/devices/device-info-tracking/system-info-track/system-info-track.component').then((c) => c.SystemInfoTrackComponent),
						canActivate: [AccessFunctionGuard],
						data: { accessFunction: pagesAccessFunctions.system_info }
					}
				]
			},
			{
				path: 'api-logs',
				loadComponent: () => import('./dashboard/logs/api-logs/api-logs.component').then((c) => c.ApiLogsComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.api_logs }
			},
			{
				path: 'fw-upload',
				loadComponent: () => import('./dashboard/admin/fw-upload/fw-upload.component').then((c) => c.FwUploadComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.fw_upload }
			},
			{
				path: 'fw-logs',
				loadComponent: () => import('./dashboard/admin/fw-log/fw-log.component').then((c) => c.FwLogComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.fw_logs }
			},
			{
				path: 'action-log',
				loadComponent: () => import('./dashboard/admin/action-log/action-log.component').then((c) => c.ActionLogComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.action_log }
			},
			{
				path: 'shipment/receive-shipment',
				loadComponent: () => import('./dashboard/admin/shipment/receive-shipment/receive-shipment.component').then((c) => c.ReceiveShipmentComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.receive_shipment }
			},
			{
				path: 'shipment/shipment-quality',
				loadComponent: () => import('./dashboard/admin/shipment/shipment-quality/shipment-quality.component').then((c) => c.ShipmentQualityComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.shipment_quality }
			},
			{
				path: 'shipment/receive-shipment-history',
				loadComponent: () => import('./dashboard/admin/shipment/shipment-history/shipment-history.component').then((c) => c.ShipmentHistoryComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.receive_shipment_history, page: 'receive-shipment-history' }
			},
			{
				path: 'shipment/shipment-quality-history',
				loadComponent: () => import('./dashboard/admin/shipment/shipment-history/shipment-history.component').then((c) => c.ShipmentHistoryComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.shipment_quality_history, page: 'shipment-quality-history' }
			},
			{
				path: 'shipment/shipping-out',
				loadComponent: () => import('./dashboard/admin/shipment/shipping-out/shipping-out.component').then((c) => c.ShippingOutComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.shipping_out }
			},
			{
				path: 'shipping/shipping-out-history',
				loadComponent: () => import('./dashboard/admin/shipment/shipping-out-history/shipping-out-history.component').then((c) => c.ShippingOutHistoryComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.shipping_out_history, page: 'shipping-out-history' }
			},
			{
				path: 'shipment/rma-devices',
				loadComponent: () => import('./dashboard/admin/shipment/rma-devices/rma-devices.component').then((c) => c.RmaDevicesComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.rma_devices }
			},
			{
				path: 'shipment/orders-management',
				loadComponent: () => import('./dashboard/admin/shipment/orders-management/orders-management.component').then((c) => c.OrdersManagementComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.orders_management }
			},
			{
				path: 'shipment/orders-management/create-order',
				loadComponent: () => import('./dashboard/admin/shipment/create-order/create-order.component').then((c) => c.CreateOrderComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.create_order }
			},
			{
				path: 'shipment/orders-management/view-order/:id',
				loadComponent: () => import('./dashboard/admin/shipment/create-order/create-order.component').then((c) => c.CreateOrderComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.view_order, page: 'view-order'}
			},
			{
				path: 'shipment/orders-management/edit-order/:id',
				loadComponent: () => import('./dashboard/admin/shipment/create-order/create-order.component').then((c) => c.CreateOrderComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.edit_order, page: 'edit-order' }
			},
			{
				path: 'firmware-update',
				loadComponent: () => import('./dashboard/admin/firmware-update/firmware-update.component').then((c) => c.FirmwareUpdateComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.firmware_update, page: 'firmware-update' }
			},
			{
				path: 'stage-firmware-update-history',
				loadComponent: () => import('./dashboard/admin/stage-firmware-update-history/stage-firmware-update-history.component').then((c) => c.StageFirmwareUpdateHistoryComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.firmware_update, page: 'firmware-update' }
			},
			{
				path: 'user-roles',
				loadComponent: () => import('./dashboard/user-roles/user-roles.component').then((c) => c.UserRolesComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.user_roles }
			},
			{
				path: 'user-roles/create',
				loadComponent: () => import('./dashboard/user-roles/create-user-role/create-user-role.component').then((c) => c.CreateUserRoleComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.user_roles, page: 'create-role' }
			},
			{
				path: 'user-roles/edit/:id',
				loadComponent: () => import('./dashboard/user-roles/create-user-role/create-user-role.component').then((c) => c.CreateUserRoleComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.user_roles, page: 'edit-role' }
			},
			{
				path: 'cm-fw-versions',
				loadComponent: () => import('./dashboard/admin/fw-versions/fw-versions.component').then((c) => c.FwVersionsComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.cm_fw_versions }
			},
			{
				path: 'schedule-run',
				loadComponent: () => import('./dashboard/admin/sched-run/sched-run.component').then((c) => c.SchedRunComponent),
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.schedule_run }

			},
			// SIM Management. The library hard-codes its own tab links to
			// `/admin-panel/sim-usage/<tab>`, so the path and the `:pageName` segment are fixed.
			{
				path: 'admin-panel/sim-usage',
				pathMatch: 'full',
				redirectTo: 'admin-panel/sim-usage/usage'
			},
			{
				path: 'admin-panel/sim-usage/:pageName',
				component: SimManagementComponent,
				canActivate: [AccessFunctionGuard],
				data: { accessFunction: pagesAccessFunctions.sim_management }
			}
		]
	},
	{
		path: 'unauthorized',
		component: UnauthorizedComponent,
		canActivate: [],
		children: []
	},
	{
		path: 'privacy-policy',
		loadComponent: () => import('./dashboard/legal/privacy-policy/privacy-policy.component').then((c) => c.PrivacyPolicyComponent),
		canActivate: [],
		children: []
	},
	{
		path: 'terms-conditions',
		loadComponent: () => import('./dashboard/legal/terms-conditions/terms-conditions.component').then((c) => c.TermsConditionsComponent),
		canActivate: [],
		children: []
	},
	{
		path: '**',
		loadComponent: () => import('./dashboard/not-found/not-found.component').then((c) => c.NotFoundComponent)
	}
];

@NgModule({
	imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })],
	exports: [RouterModule]
})
export class AppRoutingModule {}